import { useClientHandle } from '@urql/vue'
import {
  TranslationStaleStatusDocument,
  type TranslatableEntityType,
  type TranslationStaleStatus,
} from '~/graphql/operations'

export type StaleStatusArgs = {
  entityType: TranslatableEntityType
  entityId: number
  sourceLanguageId: number
  targetLanguageId: number
  sourceChannelId?: number | null
  targetChannelId?: number | null
}

const key = (a: StaleStatusArgs) =>
  `${a.entityType}:${a.entityId}:${a.sourceLanguageId}:${a.targetLanguageId}:${a.sourceChannelId ?? ''}:${a.targetChannelId ?? ''}`

// Module-level reactive cache so the title-builder of a FormKit repeater and
// the AiFillSlot inside each disclosure both see the same data and re-render
// when entries land.
const cache = reactive(new Map<string, TranslationStaleStatus>())
const inflight = new Map<string, Promise<TranslationStaleStatus | null>>()

export const useTranslationStaleStatus = () => {
  const { client } = useClientHandle()

  const fetchStatus = async (
    args: StaleStatusArgs,
  ): Promise<TranslationStaleStatus | null> => {
    const k = key(args)
    if (cache.has(k)) return cache.get(k)!
    if (inflight.has(k)) return inflight.get(k)!

    const p = (async () => {
      try {
        const result = await client
          .query(
            TranslationStaleStatusDocument,
            {
              entityType: args.entityType,
              entityId: args.entityId,
              sourceLanguageId: args.sourceLanguageId,
              targetLanguageId: args.targetLanguageId,
              sourceChannelId: args.sourceChannelId ?? null,
              targetChannelId: args.targetChannelId ?? null,
            },
            { requestPolicy: 'network-only' },
          )
          .toPromise()
        if (result.error) throw result.error
        const status = result.data?.translationStaleStatus
        if (status) cache.set(k, status)
        return status || null
      } finally {
        inflight.delete(k)
      }
    })()
    inflight.set(k, p)
    return p
  }

  const getCached = (
    args: StaleStatusArgs,
  ): TranslationStaleStatus | undefined => cache.get(key(args))

  const invalidate = (args: StaleStatusArgs) => {
    cache.delete(key(args))
  }

  const invalidateEntity = (
    entityType: TranslatableEntityType,
    entityId: number,
  ) => {
    const prefix = `${entityType}:${entityId}:`
    for (const k of Array.from(cache.keys())) {
      if (k.startsWith(prefix)) cache.delete(k)
    }
  }

  const invalidateAll = () => cache.clear()

  return {
    fetchStatus,
    getCached,
    invalidate,
    invalidateEntity,
    invalidateAll,
  }
}
